import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';
import { AuthContext } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user } = useContext(AuthContext);
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(false);

    // Socket.io 연결
    const connectSocket = useCallback((token) => {
        if (socket) {
            socket.disconnect();
        }

        // Socket.io URL에서 /api 경로 제거 (Socket.io는 루트로 연결해야 함)
        const socketUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');
        const newSocket = io(socketUrl, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000
        });

        newSocket.on('connect', () => {
            console.log('Socket.io 연결됨');
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('Socket.io 연결 해제:', reason);
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('Socket.io 연결 오류:', error.message);
            setIsConnected(false);
        });

        // 새 알림 수신
        newSocket.on('notification:new', (data) => {
            setNotifications(prev => [data.notification, ...prev]);
            setUnreadCount(data.unreadCount);

            // 브라우저 알림 (권한이 있는 경우)
            if (Notification.permission === 'granted') {
                new Notification(data.notification.title, {
                    body: data.notification.message,
                    icon: '/logo192.png'
                });
            }
        });

        // 알림 업데이트
        newSocket.on('notification:updated', (data) => {
            setNotifications(prev =>
                prev.map(n =>
                    n._id === data.notificationId
                        ? { ...n, isRead: data.isRead }
                        : n
                )
            );
            if (data.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        });

        // 전체 읽음 처리
        newSocket.on('notifications:allRead', () => {
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [socket]);

    // 연결 해제
    const disconnectSocket = useCallback(() => {
        if (socket) {
            socket.disconnect();
            setSocket(null);
            setIsConnected(false);
        }
    }, [socket]);

    // 🔥 자동 연결: 사용자 로그인 시 Socket.io 연결
    useEffect(() => {
        if (user) {
            // 로그인 상태일 때만 연결
            try {
                const auth = localStorage.getItem('auth');
                if (auth) {
                    const { token } = JSON.parse(auth);
                    if (token) {
                        console.log('✅ 사용자 로그인 감지 - Socket.io 자동 연결 시작');
                        connectSocket(token);

                        // 초기 알림 데이터 로드
                        api.get('/notifications/recent')
                            .then(response => {
                                if (response.data.success) {
                                    setNotifications(response.data.notifications);
                                    setUnreadCount(response.data.unreadCount);
                                }
                            })
                            .catch(error => console.error('초기 알림 조회 실패:', error));
                    }
                }
            } catch (error) {
                console.error('Socket 자동 연결 실패:', error);
            }
        } else {
            // 로그아웃 상태일 때 연결 해제
            console.log('❌ 사용자 로그아웃 감지 - Socket.io 연결 해제');
            disconnectSocket();
        }

        // Cleanup: 컴포넌트 언마운트 시 연결 해제
        return () => {
            if (socket) {
                console.log('🔌 NotificationProvider unmount - Socket.io 연결 해제');
                socket.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]); // user 상태 변경 시 실행

    // 알림 목록 가져오기
    const fetchNotifications = useCallback(async (options = {}) => {
        try {
            setLoading(true);
            const { page = 1, limit = 20, unreadOnly = false } = options;

            const response = await api.get('/notifications', {
                params: { page, limit, unreadOnly }
            });

            if (response.data.success) {
                if (page === 1) {
                    setNotifications(response.data.notifications);
                } else {
                    setNotifications(prev => [...prev, ...response.data.notifications]);
                }
                return response.data;
            }
        } catch (error) {
            console.error('알림 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // 최근 알림 가져오기 (드롭다운용)
    const fetchRecentNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications/recent');
            if (response.data.success) {
                setNotifications(response.data.notifications);
                setUnreadCount(response.data.unreadCount);
            }
        } catch (error) {
            console.error('최근 알림 조회 실패:', error);
        }
    }, []);

    // 읽지 않은 알림 수 가져오기
    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            if (response.data.success) {
                setUnreadCount(response.data.count);
            }
        } catch (error) {
            console.error('읽지 않은 알림 수 조회 실패:', error);
        }
    }, []);

    // 알림 읽음 처리
    const markAsRead = useCallback(async (notificationId) => {
        try {
            const response = await api.put(`/notifications/${notificationId}/read`);
            if (response.data.success) {
                setNotifications(prev =>
                    prev.map(n =>
                        n._id === notificationId
                            ? { ...n, isRead: true }
                            : n
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));

                // Socket으로도 전송 (다른 탭/기기 동기화)
                if (socket) {
                    socket.emit('notification:read', notificationId);
                }
            }
        } catch (error) {
            console.error('알림 읽음 처리 실패:', error);
        }
    }, [socket]);

    // 전체 읽음 처리
    const markAllAsRead = useCallback(async () => {
        try {
            const response = await api.put('/notifications/read-all');
            if (response.data.success) {
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                setUnreadCount(0);

                // Socket으로도 전송
                if (socket) {
                    socket.emit('notifications:readAll');
                }
            }
        } catch (error) {
            console.error('전체 알림 읽음 처리 실패:', error);
        }
    }, [socket]);

    // 알림 삭제
    const deleteNotification = useCallback(async (notificationId) => {
        try {
            const response = await api.delete(`/notifications/${notificationId}`);
            if (response.data.success) {
                const notification = notifications.find(n => n._id === notificationId);
                setNotifications(prev => prev.filter(n => n._id !== notificationId));
                if (notification && !notification.isRead) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            }
        } catch (error) {
            console.error('알림 삭제 실패:', error);
        }
    }, [notifications]);

    // 브라우저 알림 권한 요청
    const requestNotificationPermission = useCallback(async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    }, []);

    // 알림 유형별 아이콘/색상
    const getNotificationStyle = useCallback((type) => {
        const styles = {
            grading_complete: { icon: 'CircleCheck', color: '#00b894' },
            assignment_due_soon: { icon: 'Clock', color: '#fdcb6e' },
            assignment_overdue: { icon: 'TriangleAlert', color: '#d63031' },
            new_assignment: { icon: 'FileText', color: '#0984e3' },
            new_submission: { icon: 'FileText', color: '#6c5ce7' },
            feedback_received: { icon: 'MessageSquare', color: '#00b894' },
            system: { icon: 'Info', color: '#636e72' }
        };
        return styles[type] || styles.system;
    }, []);

    const value = {
        socket,
        notifications,
        unreadCount,
        isConnected,
        loading,
        connectSocket,
        disconnectSocket,
        fetchNotifications,
        fetchRecentNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        requestNotificationPermission,
        getNotificationStyle
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
